import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Phone, MessageCircle, User, Mail, PhoneCall, X, Send, MessageSquare, Star, ChevronDown } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { useCountry } from '../context/CountryContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactAdvertiser = ({ listing, ratingSummary, onOpenReviews }) => {
  const { countryConfig } = useCountry();
  // Prefer the LISTING's country currency so LK listings always show Rs. even
  // when the visitor's context is UK, and vice versa.
  const listingCountry = (listing?.country || '').toUpperCase();
  const currencySymbol = listingCountry === 'LK' || listingCountry === 'SL'
    ? 'Rs.'
    : listingCountry === 'UK' || listingCountry === 'GB'
      ? '£'
      : (countryConfig?.currency_symbol || 'Rs.');
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPhone, setShowPhone] = useState(false);
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [startingChat, setStartingChat] = useState(false);
  const [callbackForm, setCallbackForm] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+94',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Country codes list
  const countryCodes = [
    { code: '+94', country: '🇱🇰 Sri Lanka', flag: '🇱🇰' },
    { code: '+91', country: '🇮🇳 India', flag: '🇮🇳' },
    { code: '+1', country: '🇺🇸 USA', flag: '🇺🇸' },
    { code: '+44', country: '🇬🇧 UK', flag: '🇬🇧' },
    { code: '+971', country: '🇦🇪 UAE', flag: '🇦🇪' },
    { code: '+61', country: '🇦🇺 Australia', flag: '🇦🇺' },
    { code: '+65', country: '🇸🇬 Singapore', flag: '🇸🇬' },
    { code: '+60', country: '🇲🇾 Malaysia', flag: '🇲🇾' },
    { code: '+92', country: '🇵🇰 Pakistan', flag: '🇵🇰' },
    { code: '+880', country: '🇧🇩 Bangladesh', flag: '🇧🇩' },
  ];

  const handleStartChat = async () => {
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Please login to start a conversation',
        variant: 'destructive'
      });
      navigate('/login', { state: { from: `/listing/${listing.id}` } });
      return;
    }

    // Check if user is trying to chat with themselves
    if (listing.seller?.id === currentUser.id || listing.user_id === currentUser.id) {
      toast({
        title: 'Error',
        description: 'You cannot message yourself',
        variant: 'destructive'
      });
      return;
    }

    try {
      setStartingChat(true);
      
      // Track chat start
      trackEvent('chat_start');
      
      // Create or get existing conversation
      const sellerId = listing.seller?.id || listing.user_id;
      const response = await axios.post(
        `${API_URL}/api/chat/conversations`,
        { 
          listing_id: listing.id,
          seller_id: sellerId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Navigate to chat page
      navigate(`/messages/${response.data.id}`);
    } catch (error) {
      console.error('Error starting chat:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to start conversation',
        variant: 'destructive'
      });
    } finally {
      setStartingChat(false);
    }
  };

  // Track analytics event
  const trackEvent = async (eventType) => {
    try {
      await axios.post(`${API_URL}/api/analytics/track/event`, {
        listing_id: listing.id,
        event_type: eventType
      });
    } catch (error) {
      console.debug('Analytics tracking failed:', error);
    }
  };

  const handleWhatsAppClick = () => {
    // Track WhatsApp click
    trackEvent('whatsapp_click');
    
    // Get seller's phone number
    const sellerPhone = listing.seller?.phone || listing.phone;
    
    if (!sellerPhone) {
      toast({
        title: 'Phone Not Available',
        description: 'Seller phone number is not available for WhatsApp',
        variant: 'destructive'
      });
      return;
    }
    
    // Clean phone number - remove spaces, dashes, and ensure proper format
    let phone = sellerPhone.replace(/[\s\-\(\)]/g, '');
    
    // If phone doesn't start with +, assume Sri Lankan number
    if (!phone.startsWith('+')) {
      // Remove leading 0 if present and add Sri Lankan country code
      phone = phone.replace(/^0/, '');
      phone = '94' + phone;
    } else {
      // Remove the + sign for wa.me URL
      phone = phone.replace('+', '');
    }
    
    // Create pre-filled message with listing details
    const message = `Hi! I'm interested in your listing on Zeilian.co:\n\n📦 *${listing.title}*\n💰 Price: ${currencySymbol} ${listing.price?.toLocaleString() || 'N/A'}\n🔗 Link: ${window.location.href}\n\nIs this still available?`;
    
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleCallClick = () => {
    // Track phone click
    trackEvent('phone_click');
    if (showPhone) {
      window.location.href = `tel:${listing.phone}`;
    } else {
      setShowPhone(true);
    }
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      toast({
        title: 'Request Sent',
        description: 'Call back request sent successfully! The seller will contact you soon.'
      });
      setShowCallbackForm(false);
      setCallbackForm({ name: '', email: '', phone: '', countryCode: '+94', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  const maskPhoneNumber = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length <= 4) return phone;
    return digits.slice(0, -4).replace(/\d/g, 'X') + digits.slice(-4);
  };

  return (
    <>
      <Card>
        <CardContent className="p-6">
          {/* Seller Info */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center text-white">
                <User className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">For sale by {listing.seller?.name || 'Seller'}</h3>
                <p className="text-sm text-gray-500">Member since {new Date(listing.created_at).getFullYear()}</p>
              </div>
            </div>
          </div>

          {/* Price row + compact reviews preview (click chevron to jump to full reviews) */}
          <div className="mb-6 flex items-start justify-between gap-3">
            <div className="text-3xl font-bold text-gray-900 min-w-0">
              <span className="whitespace-nowrap">
                {currencySymbol}{listing.price.toLocaleString()}
              </span>
              {listing.negotiable && (
                <span className="block text-base font-normal text-green-600 mt-1">(Negotiable)</span>
              )}
            </div>

            {ratingSummary && ratingSummary.total_reviews > 0 && (
              <button
                type="button"
                onClick={onOpenReviews}
                className="group flex flex-col items-end gap-0.5 text-right rounded-md px-1.5 py-1 hover:bg-indigo-50 transition-colors"
                aria-label="View customer reviews"
                data-testid="reviews-preview-trigger"
              >
                <span className="flex items-center gap-1 text-xs font-semibold text-gray-700 group-hover:text-indigo-700">
                  Customer reviews
                  <ChevronDown className="h-3.5 w-3.5 text-gray-500 group-hover:text-indigo-600" />
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="flex" aria-label={`Rated ${ratingSummary.average_rating} out of 5`}>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={`h-3.5 w-3.5 ${s <= Math.round(ratingSummary.average_rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
                      />
                    ))}
                  </span>
                  <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
                    {ratingSummary.average_rating.toFixed(1)} out of 5
                  </span>
                </span>
              </button>
            )}
          </div>

          {/* Contact Buttons */}
          <div className="space-y-3">
            {/* Phone Button */}
            <Button
              onClick={handleCallClick}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-700 hover:from-blue-700 hover:to-purple-800 text-white py-6 text-lg font-semibold"
            >
              <Phone className="h-5 w-5 mr-2" />
              {showPhone ? listing.phone : maskPhoneNumber(listing.phone)}
            </Button>
            {showPhone && (
              <p className="text-xs text-center text-gray-500">
                Click to show phone number
              </p>
            )}

            {/* Chat Button */}
            <Button
              onClick={handleStartChat}
              disabled={startingChat}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-700 hover:from-blue-700 hover:to-purple-800 text-white py-6 text-lg font-semibold"
            >
              {startingChat ? (
                <>
                  <MessageSquare className="h-5 w-5 mr-2 animate-pulse" />
                  Starting Chat...
                </>
              ) : (
                <>
                  <MessageSquare className="h-5 w-5 mr-2" />
                  Chat with Seller
                </>
              )}
            </Button>

            {/* WhatsApp Button */}
            <Button
              onClick={handleWhatsAppClick}
              className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white py-6 text-lg font-semibold flex items-center justify-center gap-2"
            >
              <svg 
                viewBox="0 0 24 24" 
                className="h-6 w-6 fill-current"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </Button>

            {/* Request Callback Button */}
            <Button
              onClick={() => setShowCallbackForm(true)}
              variant="outline"
              className="w-full border-2 border-purple-600 text-purple-600 hover:bg-purple-50 py-6 text-lg font-semibold"
            >
              <PhoneCall className="h-5 w-5 mr-2" />
              Request a call back
            </Button>
          </div>

          {/* Safety Tips */}
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800 font-medium mb-1">Safety Tips</p>
            <ul className="text-xs text-yellow-700 space-y-1">
              <li>• Meet in a safe public place</li>
              <li>• Never transfer money in advance</li>
              <li>• Inspect the item before purchase</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Call Back Request Modal */}
      <Dialog open={showCallbackForm} onOpenChange={setShowCallbackForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Request a call back
            </DialogTitle>
            <DialogDescription>
              Please enter your phone number to request the advertiser to call you back
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCallbackSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="callback-name">Your Name *</Label>
              <Input
                id="callback-name"
                placeholder="Enter your name"
                value={callbackForm.name}
                onChange={(e) => setCallbackForm({ ...callbackForm, name: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="callback-email">Email Address *</Label>
              <Input
                id="callback-email"
                type="email"
                placeholder="your.email@example.com"
                value={callbackForm.email}
                onChange={(e) => setCallbackForm({ ...callbackForm, email: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="callback-phone">Contact Number *</Label>
              <div className="flex gap-2">
                <Select 
                  value={callbackForm.countryCode} 
                  onValueChange={(value) => setCallbackForm({ ...callbackForm, countryCode: value })}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {countryCodes.map((item) => (
                      <SelectItem key={item.code} value={item.code}>
                        {item.flag} {item.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  id="callback-phone"
                  placeholder="771234567"
                  value={callbackForm.phone}
                  onChange={(e) => setCallbackForm({ ...callbackForm, phone: e.target.value })}
                  required
                  className="flex-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="callback-message">Message (Optional)</Label>
              <Textarea
                id="callback-message"
                placeholder="Add any specific questions or preferred call time..."
                value={callbackForm.message}
                onChange={(e) => setCallbackForm({ ...callbackForm, message: e.target.value })}
                rows={4}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setShowCallbackForm(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                {isSubmitting ? (
                  <>
                    <Send className="h-4 w-4 mr-2 animate-pulse" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Request
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContactAdvertiser;
